/**
 * NetWorthChart — Shared SVG area chart with X/Y axes.
 *
 * Props:
 *   dataPoints: Array<{ date: string, balance: number }>  (sorted by date ASC)
 *   height:     number  (SVG viewBox height, default 220)
 *   compact:    boolean (smaller axes labels for dashboard embed, default false)
 *   id:         string  (unique id prefix for gradients — needed when multiple charts on page)
 *
 * All rendering is pure SVG — no third-party chart lib.
 * Dates are formatted as short month labels on the X axis.
 * Dollar amounts are formatted with K/M abbreviations on the Y axis.
 */

import React from 'react';
import { parseDateValue } from '../../utils/formatters';
import styles from './NetWorthChart.module.css';

/* ---- Formatting helpers ---- */
// Exported for CashFlowChart, which shares this axis's K/M abbreviation style.
export function formatAxisDollar(value) {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function formatShortDate(dateStr) {
  if (!dateStr) return '';
  // Calendar date, not an instant: new Date('2024-06-01') is UTC midnight,
  // which is May 31 west of UTC and mislabels the month.
  const d = parseDateValue(dateStr);
  if (!d) return dateStr.slice(0, 7);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
}

/* ---- Compute nice axis tick values ---- */
// Exported for CashFlowChart, which needs the same pos/neg-domain tick logic.
export function niceAxisTicks(min, max, tickCount = 5) {
  const range = max - min || 1;
  const rawStep = range / (tickCount - 1);
  // Round step to a nice number
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const residual = rawStep / magnitude;
  let niceStep;
  if (residual <= 1.5) niceStep = magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  const niceMin = Math.floor(min / niceStep) * niceStep;
  const niceMax = Math.ceil(max / niceStep) * niceStep;

  const ticks = [];
  for (let v = niceMin; v <= niceMax + niceStep * 0.01; v += niceStep) {
    ticks.push(Math.round(v * 100) / 100);
  }
  return { ticks, niceMin, niceMax };
}

/* ---- Key date helpers ---- */
const CONFIDENCE_COLOR = {
  confirmed: 'var(--color-success-600)',
  estimated: 'var(--color-warning-600)',
  asserted: 'var(--color-text-secondary, var(--color-navy-700))',
};

function parseDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToX(date, firstDate, lastDate, marginLeft, plotW) {
  const t = (date.getTime() - firstDate.getTime()) / (lastDate.getTime() - firstDate.getTime());
  return marginLeft + t * plotW;
}

/* ---- Key date label sizing ----
 * Key date labels render rotated above the plot, so a long label's vertical
 * footprint is its pixel width * sin(rotation). The lane used to be sized from the
 * longest label with no upper bound, which let one long milestone eat the whole
 * chart: a 43-char label left a 13px plot inside the 200px dashboard chart, and
 * a 48-char label drove the plot height negative.
 *
 * The lane is now capped so the plot always keeps MIN_PLOT_RATIO of the chart
 * height, and labels are truncated to what the capped lane can show. The full
 * text stays available in the <title> tooltip.
 */
// Font sizes are duplicated from NetWorthChart.module.css because SVG text has
// no layout box to measure — the label geometry below is computed, so these two
// must be changed together.
const KEY_DATE_FONT_PX = 9;
const X_AXIS_FONT_PX = 10;
const GLYPH_W_RATIO = 0.55; // avg glyph width as a fraction of font size
const KEY_DATE_LABEL_PAD = 8; // breathing room between the label and the plot
const KEY_DATE_LANE_MIN = 22;
const MIN_PLOT_RATIO = 0.6;

/* Label rotation. A rotated label's vertical footprint is width * sin(angle)
 * and its horizontal footprint is width * cos(angle), so a shallower angle buys
 * visible characters inside the capped lane at the cost of running wider.
 *
 * 25 degrees is chosen so the labels that a divorce case almost always carries
 * fit untruncated in the capped lane: "Date of Separation" (18 chars) needs a
 * 46px lane against the 52px the dashboard chart allows. 45 degrees allowed
 * only 12 characters and 30 degrees only 17, both of which truncate it.
 *
 * Every consumer below derives from this one constant — the lane height, the
 * truncation budget, the right-edge flip test and the SVG transform — so the
 * geometry cannot drift out of sync with what is actually drawn.
 */
const KEY_DATE_ROTATION_DEG = 25;
const KEY_DATE_SIN = Math.sin((KEY_DATE_ROTATION_DEG * Math.PI) / 180);
const KEY_DATE_COS = Math.cos((KEY_DATE_ROTATION_DEG * Math.PI) / 180);

function keyDateLabelWidthPx(chars) {
  return chars * KEY_DATE_FONT_PX * GLYPH_W_RATIO;
}

function laneForChars(chars) {
  return Math.ceil(keyDateLabelWidthPx(chars) * KEY_DATE_SIN) + KEY_DATE_LABEL_PAD;
}

function charsForLane(lane) {
  const widthPx = Math.max(0, lane - KEY_DATE_LABEL_PAD) / KEY_DATE_SIN;
  return Math.floor(widthPx / (KEY_DATE_FONT_PX * GLYPH_W_RATIO));
}

function truncateKeyDateLabel(label, maxChars) {
  const text = label || '';
  if (maxChars <= 0) return '';
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}

/* A negatively-rotated label anchored at its start runs up and to the RIGHT, so
 * markers near the right edge overflow the viewBox and get clipped. Mirror those
 * to the positive angle anchored at the end, which runs up and to the LEFT —
 * still clear of the plot, never clipped.
 */
function keyDateLabelOverflowsRight(x, chars, viewWidth, marginRight) {
  return x + keyDateLabelWidthPx(chars) * KEY_DATE_COS > viewWidth - marginRight;
}

/* ---- Main Component ---- */
export default function NetWorthChart({
  dataPoints,
  height = 220,
  compact = false,
  id = 'nwc',
  keyDates,
}) {
  if (!dataPoints || dataPoints.length < 2) return null;

  // Layout constants. Horizontal margins and plotW are independent of the key
  // date lane, so key dates resolve first and the lane is sized from the labels
  // that actually render (an out-of-range key date must not inflate the lane).
  const marginLeft = compact ? 55 : 65;
  const marginRight = 16;
  const marginBottom = compact ? 28 : 32;
  const viewWidth = 800;
  const viewHeight = height;
  const plotW = viewWidth - marginLeft - marginRight;

  // Key dates — filter to chart date range and compute x positions
  const visibleKeyDates = (() => {
    if (!keyDates || keyDates.length === 0) return [];
    const firstDate = parseDate(dataPoints[0].date);
    const lastDate = parseDate(dataPoints[dataPoints.length - 1].date);
    if (!firstDate || !lastDate || firstDate.getTime() === lastDate.getTime()) return [];

    return keyDates
      .map((kd) => {
        const start = parseDate(kd.start_date);
        if (!start) return null;
        if (start.getTime() < firstDate.getTime() || start.getTime() > lastDate.getTime()) return null;
        const x = dateToX(start, firstDate, lastDate, marginLeft, plotW);
        let endX = null;
        if (kd.kind === 'span' && kd.end_date) {
          const end = parseDate(kd.end_date);
          if (end && end.getTime() > start.getTime()) {
            const clampedEnd = new Date(Math.min(end.getTime(), lastDate.getTime()));
            endX = dateToX(clampedEnd, firstDate, lastDate, marginLeft, plotW);
          }
        }
        return { ...kd, x, endX, startDate: start };
      })
      .filter(Boolean);
  })();

  // Key date label lane, capped so the plot keeps at least MIN_PLOT_RATIO of the
  // chart height no matter how long a milestone label is.
  const maxKeyDateLane = Math.max(
    KEY_DATE_LANE_MIN,
    Math.floor(viewHeight * (1 - MIN_PLOT_RATIO)) - marginBottom
  );
  const keyDateMaxChars = charsForLane(maxKeyDateLane);
  const longestVisibleChars = visibleKeyDates.length
    ? Math.min(
        Math.max(0, ...visibleKeyDates.map((kd) => (kd.label || '').length)),
        keyDateMaxChars
      )
    : 0;
  const marginTop = visibleKeyDates.length
    ? Math.max(KEY_DATE_LANE_MIN, laneForChars(longestVisibleChars))
    : 12;
  const plotH = viewHeight - marginTop - marginBottom;

  const balances = dataPoints.map((p) => p.balance);
  const rawMin = Math.min(...balances);
  const rawMax = Math.max(...balances);
  // Always include zero in the domain so the baseline is visible when values cross or stay below zero
  const domainMin = Math.min(rawMin, 0);
  const domainMax = Math.max(rawMax, 0);
  const { ticks: yTicks, niceMin, niceMax } = niceAxisTicks(domainMin, domainMax, compact ? 4 : 5);
  const yRange = niceMax - niceMin || 1;

  // Map data to pixel coords
  const coords = balances.map((val, i) => {
    const x = marginLeft + (i / (balances.length - 1)) * plotW;
    const y = marginTop + plotH - ((val - niceMin) / yRange) * plotH;
    return { x, y };
  });

  // Zero baseline pixel y (clamped to plot area)
  const zeroY = marginTop + plotH - ((0 - niceMin) / yRange) * plotH;
  const clampedZeroY = Math.max(marginTop, Math.min(marginTop + plotH, zeroY));

  // Determine sign based on latest value (drives color)
  const latestBalance = balances[balances.length - 1];
  const isNegative = latestBalance < 0;

  // Smooth curve path
  let linePath = `M${coords[0].x},${coords[0].y}`;
  for (let i = 1; i < coords.length; i++) {
    const prev = coords[i - 1];
    const curr = coords[i];
    const cpx1 = prev.x + (curr.x - prev.x) * 0.4;
    const cpx2 = prev.x + (curr.x - prev.x) * 0.6;
    linePath += ` C${cpx1},${prev.y} ${cpx2},${curr.y} ${curr.x},${curr.y}`;
  }

  // Area fill closes to the zero baseline (not the plot bottom), so sign is visually obvious
  const areaPath = `${linePath} L${coords[coords.length - 1].x},${clampedZeroY} L${coords[0].x},${clampedZeroY} Z`;

  const lineColor = isNegative ? 'var(--color-error-500, #dc2626)' : 'var(--color-accent-500)';
  const areaStopColor = isNegative ? 'var(--color-error-400, #ef4444)' : 'var(--color-accent-400)';

  // X-axis: pick evenly spaced date labels (max ~6-8 labels)
  const maxXLabels = compact ? 5 : 8;
  const xLabelStep = Math.max(1, Math.ceil(dataPoints.length / maxXLabels));
  const xLabels = [];
  for (let i = 0; i < dataPoints.length; i += xLabelStep) {
    xLabels.push({ index: i, label: formatShortDate(dataPoints[i].date) });
  }
  // Always include last point
  if (xLabels.length === 0 || xLabels[xLabels.length - 1].index !== dataPoints.length - 1) {
    xLabels.push({ index: dataPoints.length - 1, label: formatShortDate(dataPoints[dataPoints.length - 1].date) });
  }

  const gradientId = `${id}_areaGrad`;

  return (
    <svg
      className={styles.chartSvg}
      viewBox={`0 0 ${viewWidth} ${viewHeight}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Net worth trend chart"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={areaStopColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={areaStopColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Key date span bands — behind everything */}
      {visibleKeyDates
        .filter((kd) => kd.kind === 'span' && kd.endX != null)
        .map((kd) => {
          const fill = CONFIDENCE_COLOR[kd.confidence] || CONFIDENCE_COLOR.asserted;
          return (
            <rect
              key={`kd-band-${kd.dateID}`}
              x={kd.x}
              y={marginTop}
              width={Math.max(kd.endX - kd.x, 1)}
              height={plotH}
              fill={fill}
              fillOpacity="0.10"
            >
              <title>{`${kd.label} (${kd.confidence || 'asserted'})`}</title>
            </rect>
          );
        })}

      {/* Y-axis grid lines + labels */}
      {yTicks.map((tick) => {
        const y = marginTop + plotH - ((tick - niceMin) / yRange) * plotH;
        return (
          <g key={tick}>
            <line
              x1={marginLeft}
              y1={y}
              x2={viewWidth - marginRight}
              y2={y}
              className={styles.gridLine}
              strokeWidth="1"
            />
            <text x={marginLeft - 8} y={y} className={styles.yAxisLabel}>
              {formatAxisDollar(tick)}
            </text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaPath} fill={`url(#${gradientId})`} />

      {/* Zero baseline (only draw if 0 is inside the visible y domain) */}
      {niceMin < 0 && niceMax > 0 && (
        <line
          x1={marginLeft}
          y1={clampedZeroY}
          x2={viewWidth - marginRight}
          y2={clampedZeroY}
          stroke="var(--color-gray-400)"
          strokeWidth="1"
          strokeDasharray="2 3"
        />
      )}

      {/* Line */}
      <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2.5" />

      {/* Key date point markers — vertical dashed lines + labels */}
      {visibleKeyDates
        .filter((kd) => !(kd.kind === 'span' && kd.endX != null))
        .map((kd) => {
          const stroke = CONFIDENCE_COLOR[kd.confidence] || CONFIDENCE_COLOR.asserted;
          const text = truncateKeyDateLabel(kd.label, keyDateMaxChars);
          const labelY = marginTop - 4;
          const flip = keyDateLabelOverflowsRight(kd.x, text.length, viewWidth, marginRight);
          const tooltip = `${kd.label} — ${formatShortDate(kd.start_date)}${kd.confidence ? ` (${kd.confidence})` : ''}`;
          return (
            <g key={`kd-marker-${kd.dateID}`}>
              <line
                x1={kd.x}
                y1={marginTop}
                x2={kd.x}
                y2={marginTop + plotH}
                stroke={stroke}
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.7"
              >
                <title>{tooltip}</title>
              </line>
              <text
                x={kd.x}
                y={labelY}
                transform={`rotate(${flip ? KEY_DATE_ROTATION_DEG : -KEY_DATE_ROTATION_DEG} ${kd.x} ${labelY})`}
                className={flip ? `${styles.keyDateLabel} ${styles.keyDateLabelFlipped}` : styles.keyDateLabel}
                fill={stroke}
              >
                {text}
                <title>{tooltip}</title>
              </text>
            </g>
          );
        })}

      {/* Key date span labels */}
      {visibleKeyDates
        .filter((kd) => kd.kind === 'span' && kd.endX != null)
        .map((kd) => {
          const fill = CONFIDENCE_COLOR[kd.confidence] || CONFIDENCE_COLOR.asserted;
          const midX = kd.x + (kd.endX - kd.x) / 2;
          const text = truncateKeyDateLabel(kd.label, keyDateMaxChars);
          const labelY = marginTop - 4;
          const flip = keyDateLabelOverflowsRight(midX, text.length, viewWidth, marginRight);
          return (
            <text
              key={`kd-slabel-${kd.dateID}`}
              x={midX}
              y={labelY}
              transform={`rotate(${flip ? KEY_DATE_ROTATION_DEG : -KEY_DATE_ROTATION_DEG} ${midX} ${labelY})`}
              className={flip ? `${styles.keyDateLabel} ${styles.keyDateLabelFlipped}` : styles.keyDateLabel}
              fill={fill}
            >
              {text}
              <title>{`${kd.label} (${kd.confidence || 'asserted'})`}</title>
            </text>
          );
        })}

      {/* X-axis labels */}
      {xLabels.map(({ index, label }) => {
        // text-anchor is middle, so clamp by half the label width to keep the
        // first and last labels inside the viewBox instead of clipping them.
        const halfW = (label.length * X_AXIS_FONT_PX * GLYPH_W_RATIO) / 2;
        const rawX = marginLeft + (index / (dataPoints.length - 1)) * plotW;
        const x = Math.min(Math.max(rawX, halfW), viewWidth - halfW);
        const y = marginTop + plotH + 14;
        return (
          <text key={index} x={x} y={y} className={styles.xAxisLabel}>
            {label}
          </text>
        );
      })}

      {/* Bottom axis line */}
      <line
        x1={marginLeft}
        y1={marginTop + plotH}
        x2={viewWidth - marginRight}
        y2={marginTop + plotH}
        stroke="var(--color-gray-200)"
        strokeWidth="1"
      />
    </svg>
  );
}
